import React, { useState, useEffect, useRef } from 'react';
import { rankingAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Alert, AlertDescription } from '../ui/alert';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import { Trophy, Crown, Medal, TrendingUp, Gift, Clock, Loader2, Wifi, WifiOff } from 'lucide-react';
import { toast } from 'sonner';
import { FoxSpinnerInline } from '../ui/FoxSpinner';

const ClientRanking = ({ kakoId, userInfo, initialRanking = [], showCurrentRanking = true, showPrizes = false, hasPendingPrizes = false }) => {
  const [topRankings, setTopRankings] = useState(initialRanking);
  const [myPosition, setMyPosition] = useState(null);
  const [winnersHistory, setWinnersHistory] = useState([]);
  const [myPrizes, setMyPrizes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState({});
  const [timeUntilReset, setTimeUntilReset] = useState(0);
  const [rankingConfig, setRankingConfig] = useState(null);
  const [previousWinners, setPreviousWinners] = useState([]); // NOVO - ganhadores anteriores
  const [showRules, setShowRules] = useState(false);
  const [wsConnected, setWsConnected] = useState(false);
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // WebSocket para atualizações em tempo real
  useEffect(() => {
    if (!showCurrentRanking) return;
    
    connectWebSocket();
    
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, [showCurrentRanking]);
  
  const connectWebSocket = () => {
    try {
      const wsUrl = process.env.REACT_APP_BACKEND_URL
        .replace('https://', 'wss://')
        .replace('http://', 'ws://');
      
      const ws = new WebSocket(`${wsUrl}/api/ws/ranking`);
      wsRef.current = ws;
      
      ws.onopen = () => {
        console.log('🔌 Ranking WebSocket conectado');
        setWsConnected(true);
      };
      
      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'initial_data') {
            // Dados iniciais recebidos
            if (data.rankings && data.rankings.length > 0) {
              setTopRankings(data.rankings);
            }
          } else if (data.type === 'ranking_update') {
            // Ranking atualizado em tempo real
            if (data.ranking && data.ranking.top_10) {
              setTopRankings(data.ranking.top_10);
              
              // Atualiza minha posição se sou eu que comprei
              if (kakoId && data.ranking.updated_user?.kako_id === kakoId) {
                loadMyPosition();
              }
            }
          }
        } catch (e) {
          console.error('Erro ao processar mensagem WS:', e);
        }
      };
      
      ws.onclose = () => {
        console.log('🔌 Ranking WebSocket desconectado');
        setWsConnected(false);
        // Reconecta após 5 segundos
        reconnectTimeoutRef.current = setTimeout(() => {
          if (showCurrentRanking) connectWebSocket();
        }, 5000);
      };
      
      ws.onerror = (error) => {
        console.error('Ranking WebSocket error:', error);
        setWsConnected(false);
      };
      
      // Ping a cada 30 segundos
      const pingInterval = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.send('ping');
        }
      }, 30000);
      
      ws.addEventListener('close', () => clearInterval(pingInterval));
      
    } catch (error) {
      console.error('Erro ao conectar Ranking WebSocket:', error);
    }
  };
  
  const loadMyPosition = async () => {
    if (!kakoId) return;
    try {
      const positionRes = await rankingAPI.getMyPosition(kakoId);
      setMyPosition(positionRes.data);
    } catch (error) {
      console.error('Erro ao carregar posição:', error);
    }
  };

  // Cronômetro até meia-noite
  useEffect(() => {
    const calculateTime = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      return Math.floor((midnight - now) / 1000);
    };

    setTimeUntilReset(calculateTime());
    const timer = setInterval(() => {
      const seconds = calculateTime();
      setTimeUntilReset(seconds);
      if (seconds <= 0) {
        clearInterval(timer);
        loadRankingData();
      }
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (kakoId) {
      setLoading(true);
      loadRankingData();
    }
  }, [kakoId]);

  useEffect(() => {
    if (initialRanking.length > 0) {
      setTopRankings(initialRanking);
    }
  }, [initialRanking]);

  useEffect(() => {
    loadPreviousWinners(); // NOVO - Carregar ganhadores anteriores
  }, [kakoId]);

  const loadPreviousWinners = async () => {
    try {
      const response = await rankingAPI.getWinners(10); // Últimos 10 ganhadores
      setPreviousWinners(response.data);
    } catch (error) {
      console.log('Erro ao carregar ganhadores anteriores:', error);
    }
  };

  const loadRankingData = async () => {
    console.log('🏆 Carregando dados do ranking...');
    try {
      const [rankingsRes, positionRes, winnersRes, prizesRes, configRes] = await Promise.all([
        rankingAPI.getCurrent(10),
        rankingAPI.getMyPosition(kakoId),
        rankingAPI.getWinners(10),
        rankingAPI.getMyPrizes(kakoId),
        rankingAPI.getPrizesInfo()  // Rota pública (sem auth)
      ]);
      
      console.log('✅ Ranking atual:', rankingsRes.data);
      console.log('✅ Minha posição:', positionRes.data);
      console.log('✅ Ganhadores:', winnersRes.data);
      console.log('✅ Meus prêmios:', prizesRes.data);
      console.log('✅ Configuração:', configRes.data);
      
      setTopRankings(rankingsRes.data);
      setMyPosition(positionRes.data);
      setRankingConfig(configRes.data);
      console.log('📋 Config de prêmios:', configRes.data);
      console.log('📋 Prêmios disponíveis:', configRes.data?.prizes);
      
      // Filtrar apenas ganhadores de ONTEM
      const now = new Date();
      const yesterday = new Date(now);
      yesterday.setDate(yesterday.getDate() - 1);
      const yesterdayStr = yesterday.getFullYear() + '-' + 
                    String(yesterday.getMonth() + 1).padStart(2, '0') + '-' + 
                    String(yesterday.getDate()).padStart(2, '0');
      
      const yesterdayWinners = winnersRes.data.filter(w => w.date === yesterdayStr);
      setWinnersHistory(yesterdayWinners);
      
      setMyPrizes(prizesRes.data);
    } catch (error) {
      console.error('Erro ao carregar ranking:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClaimPrize = async (date) => {
    setClaiming({ ...claiming, [date]: true });
    try {
      const response = await rankingAPI.claimPrize(kakoId, date);
      toast.success(`🎉 ${response.data.message}`);
      loadRankingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao resgatar prêmio');
    } finally {
      setClaiming({ ...claiming, [date]: false });
    }
  };

  const getMedalIcon = (position) => {
    if (position === 1) return { icon: Crown, color: 'text-yellow-500', bg: 'bg-gradient-to-br from-yellow-100 to-yellow-200', label: 'OURO' };
    if (position === 2) return { icon: Medal, color: 'text-gray-400', bg: 'bg-gradient-to-br from-gray-200 to-gray-300', label: 'PRATA' };
    if (position === 3) return { icon: Medal, color: 'text-orange-600', bg: 'bg-gradient-to-br from-orange-100 to-orange-200', label: 'BRONZE' };
    return { icon: Trophy, color: 'text-purple-500', bg: 'bg-gray-50', label: '' };
  };

  const getPrizeForPosition = (position, userTotalCoins = null) => {
    if (!rankingConfig || !rankingConfig.prizes) {
      console.log('⚠️ Sem config de prêmios:', rankingConfig);
      return null;
    }
    const prize = rankingConfig.prizes.find(p => p.position === position);
    if (!prize) return null;
    
    // Calcula baseado no tipo
    const prizeType = prize.type || 'fixed';
    const prizeValue = prize.value || prize.coins || 0;
    
    if (prizeType === 'percentage' && userTotalCoins) {
      // Calcula percentual das moedas compradas
      const calculated = Math.floor((userTotalCoins * prizeValue) / 100);
      console.log(`🎁 Prêmio ${position}º: ${prizeValue}% de ${userTotalCoins} = ${calculated} moedas`);
      return calculated;
    }
    
    console.log(`🎁 Prêmio ${position}º (fixo):`, prizeValue);
    return prizeValue;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8">
          <FoxSpinnerInline size="md" />
        </CardContent>
      </Card>
    );
  }

  const isInTop10 = myPosition && myPosition.position > 0 && myPosition.position <= 10;

  return (
    <Card data-testid="client-ranking-card" className="border-yellow-200">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Trophy className="text-yellow-500" />
            <CardTitle>
              {showCurrentRanking ? 'Ranking Diário' : 'Meus Prêmios'}
            </CardTitle>
            {showCurrentRanking && (
              <span title={wsConnected ? 'Tempo real ativo' : 'Reconectando...'}>
                {wsConnected ? (
                  <Wifi className="w-4 h-4 text-green-500" />
                ) : (
                  <WifiOff className="w-4 h-4 text-gray-400 animate-pulse" />
                )}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowRules(true)}
              data-testid="rules-button"
            >
              📋 Regras
            </Button>
            {showCurrentRanking && (
              <div className="text-right">
                <p className="text-xs text-gray-500">Finaliza em:</p>
                <div className="text-lg font-bold text-orange-600" data-testid="ranking-reset-timer">
                  {Math.floor(timeUntilReset / 3600)}h {Math.floor((timeUntilReset % 3600) / 60)}m {timeUntilReset % 60}s
                </div>
              </div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {myPrizes.length > 0 && (
          <Alert className="mb-4 bg-yellow-50 border-yellow-300">
            <Gift className="h-4 w-4 text-yellow-600" />
            <AlertDescription className="text-yellow-800">
              <strong>🎉 Você tem {myPrizes.length} prêmio(s) para resgatar!</strong>
              {showCurrentRanking && <><br />Veja na aba "Meus Prêmios"</>}
            </AlertDescription>
          </Alert>
        )}

        {showCurrentRanking || showPrizes ? (
          <Tabs defaultValue={showCurrentRanking ? "ranking" : "prizes"} className="w-full">
            <TabsList className={`grid w-full ${showCurrentRanking && showPrizes ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {showCurrentRanking && (
                <TabsTrigger value="ranking" className="bg-orange-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white">
                  <Trophy className="w-4 h-4 mr-2" />
                  Top 10 de Hoje
                </TabsTrigger>
              )}
              {showPrizes && (
                <TabsTrigger value="prizes" className="bg-yellow-200 data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-blue-600 data-[state=active]:text-white">
                  <Gift className="w-4 h-4 mr-2" />
                  Meus Prêmios {myPrizes.length > 0 && `(${myPrizes.length})`}
                </TabsTrigger>
              )}
            </TabsList>

            {showCurrentRanking && (
            <TabsContent value="ranking" className="mt-4">
            <div className="space-y-3">
              {console.log('🔍 Renderizando ranking. topRankings:', topRankings)}
              {topRankings.length === 0 ? (
                <div>
                  <p className="text-center text-gray-500 py-8">Nenhum dado no ranking de hoje</p>
                  <p className="text-xs text-center text-gray-400">Debug: Array vazio - {topRankings.length} itens</p>
                </div>
              ) : (
                topRankings.map((user, idx) => {
                  const position = idx + 1;
                  const medal = getMedalIcon(position);
                  const MedalIcon = medal.icon;
                  const isCurrentUser = user.kako_id === kakoId;

                  return (
                    <div
                      key={user.kako_id}
                      data-testid={`ranking-position-${position}`}
                      className={`
                        ${position <= 3 ? medal.bg + ' border-2 border-yellow-400 light-sweep' : 'bg-white border-2 border-gray-200'}
                        ${isCurrentUser ? 'ring-2 sm:ring-4 ring-purple-500' : ''}
                        rounded-lg p-3 sm:p-4 transition-all hover:shadow-md
                      `}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        {/* Remover medalhas do top 3 */}
                        {position > 3 && (
                          <div className="flex-shrink-0 text-center">
                            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-purple-100 flex items-center justify-center">
                              <span className="text-sm sm:text-lg font-bold text-purple-600">{position}º</span>
                            </div>
                          </div>
                        )}

                        {/* Avatar com moldura para top 3 */}
                        {user.kako_avatar && (
                          position === 1 ? (
                            <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center">
                              <img src={user.kako_avatar} alt={user.kako_nickname} className="w-11 h-11 sm:w-14 sm:h-14 rounded-full absolute z-0" />
                              <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/xxtsr3u6_Emblema%20de%20Ouro%20e%20Coroa%20Real.png" alt="1º Lugar" className="w-full h-full object-contain absolute inset-0 z-10" />
                            </div>
                          ) : position === 2 ? (
                            <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center">
                              <img 
                                src={user.kako_avatar} 
                                alt={user.kako_nickname} 
                                className="rounded-full absolute z-0" 
                                style={{ marginTop: '-11px', width: '48px', height: '48px' }}
                              />
                              <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/ebjdmsbn_IMG_7340.PNG" alt="2º Lugar" className="w-full h-full object-contain absolute inset-0 z-10" />
                            </div>
                          ) : position === 3 ? (
                            <div className="relative w-28 h-28 sm:w-32 sm:h-32 flex items-center justify-center">
                              <img 
                                src={user.kako_avatar} 
                                alt={user.kako_nickname} 
                                className="w-11 h-11 sm:w-14 sm:h-14 rounded-full absolute z-0" 
                                style={{ marginTop: '-19px' }}
                              />
                              <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/ez6yownk_IMG_7341.PNG" alt="3º Lugar" className="w-full h-full object-contain absolute inset-0 z-10" />
                            </div>
                          ) : (
                            <img
                              src={user.kako_avatar}
                              alt={user.kako_nickname}
                              className="w-10 h-10 sm:w-12 sm:h-12 rounded-full border-2 border-white shadow"
                            />
                          )
                        )}

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                            <p className="font-bold text-sm sm:text-lg truncate">{user.kako_nickname}</p>
                            {isCurrentUser && <Badge className="bg-purple-600 text-xs">VOCÊ</Badge>}
                          </div>
                          <p className="text-xs sm:text-sm text-gray-600">{user.order_count} pedidos</p>
                          {(() => {
                            const prize = getPrizeForPosition(position, user.total_coins);
                            console.log(`Prêmio para posição ${position}:`, prize);
                            return prize && prize > 0 ? (
                              <p className="text-xs text-yellow-600 font-bold mt-1">
                                🎁 Prêmio: +{prize.toLocaleString()} moedas
                              </p>
                            ) : null;
                          })()}
                        </div>

                        <div className="text-right flex-shrink-0">
                          <p className="text-lg sm:text-2xl font-bold text-purple-600">
                            {user.total_coins} 🪙
                          </p>
                          <p className="text-[10px] sm:text-xs text-gray-600">moedas</p>
                        </div>
                      </div>
                    </div>
                  );
                })
              )}

              {!isInTop10 && myPosition && myPosition.position > 0 && (
                <div className="mt-6 pt-4 border-t-2 border-dashed border-purple-300">
                  <p className="text-sm text-gray-600 text-center mb-3">Sua Posição no Ranking:</p>
                  <div className="bg-gradient-to-r from-purple-100 to-blue-100 border-2 border-purple-400 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-full bg-purple-500 flex items-center justify-center">
                        <span className="text-xl font-bold text-white">{myPosition.position}º</span>
                      </div>
                      {userInfo?.avatar && (
                        <img
                          src={userInfo.avatar}
                          alt={userInfo.nickname}
                          className="w-12 h-12 rounded-full border-2 border-white shadow"
                        />
                      )}
                      <div className="flex-1">
                        <p className="font-bold text-lg">{userInfo?.nickname || 'Você'}</p>
                        <p className="text-sm text-gray-600">{myPosition.order_count} pedidos</p>
                      </div>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-purple-600">
                          {myPosition.total_coins} 🪙
                        </p>
                        <p className="text-xs text-gray-600">moedas</p>
                      </div>
                    </div>
                    {myPosition.position > 10 && topRankings[9] && (
                      <div className="mt-3 pt-3 border-t border-purple-300">
                        <p className="text-xs text-gray-600">
                          Para Top 10: Faltam {(topRankings[9].total_coins - myPosition.total_coins + 1)} moedas
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {myPosition && myPosition.position === 0 && (
                <div className="mt-6 pt-4 border-t-2 border-dashed border-gray-300">
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <TrendingUp className="w-12 h-12 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-600 font-medium">Você ainda não está no ranking</p>
                    <p className="text-sm text-gray-500 mt-1">Faça sua primeira recarga para aparecer!</p>
                  </div>
                </div>
              )}
            </div>
          </TabsContent>
          )}

          {showPrizes && (
          <TabsContent value="prizes" className="mt-4">
            <div className="space-y-4">
              {myPrizes.length > 0 && (
                <div className="space-y-3">
                  <h3 className="font-bold text-base sm:text-lg">🎁 Prêmios Disponíveis</h3>
                  {myPrizes.map((prize) => {
                    const expiresAt = new Date(prize.prize_expires_at);
                    const hoursLeft = Math.floor((expiresAt - new Date()) / (1000 * 60 * 60));
                    const isExpiringSoon = hoursLeft < 6;

                    return (
                      <div
                        key={prize.id}
                        className={`p-3 sm:p-4 border-2 rounded-lg ${
                          isExpiringSoon ? 'border-red-300 bg-red-50' : 'border-yellow-300 bg-yellow-50'
                        }`}
                      >
                        <div className="space-y-3">
                          {/* Título e Badge - Empilhados em mobile */}
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                            <div className="flex-1">
                              <p className="font-bold text-sm sm:text-lg">{prize.prize_description}</p>
                              <p className="text-xs sm:text-sm text-gray-600 mt-1">
                                Dia: {prize.date.split('-').reverse().join('/')} • {prize.position}º lugar
                              </p>
                            </div>
                            <Badge className={`${isExpiringSoon ? 'bg-red-600' : 'bg-yellow-600'} text-xs sm:text-sm self-start sm:self-auto`}>
                              {prize.prize_coins.toLocaleString()} 🎁
                            </Badge>
                          </div>

                          {/* Alerta de Expiração - Compacto */}
                          <Alert className={`${isExpiringSoon ? 'bg-red-100 border-red-200' : 'bg-blue-50 border-blue-200'} py-2`}>
                            <Clock className="h-3 w-3 sm:h-4 sm:w-4" />
                            <AlertDescription className="text-[10px] sm:text-xs">
                              {isExpiringSoon ? (
                                <span className="text-red-800 font-bold">
                                  ⚠️ Expira em {hoursLeft}h
                                </span>
                              ) : (
                                <span>Expira: {expiresAt.toLocaleDateString('pt-BR')} {expiresAt.toLocaleTimeString('pt-BR', {hour: '2-digit', minute: '2-digit'})}</span>
                              )}
                            </AlertDescription>
                          </Alert>

                          {/* Botão Resgatar */}
                          <Button
                            onClick={() => handleClaimPrize(prize.date)}
                            disabled={claiming[prize.date]}
                            className={`w-full text-sm sm:text-base ${
                              isExpiringSoon ? 'bg-red-600 hover:bg-red-700 animate-pulse' : 'bg-yellow-600 hover:bg-yellow-700'
                            }`}
                          >
                            {claiming[prize.date] ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Resgatando...
                              </>
                            ) : (
                              `🎁 Resgatar ${prize.prize_coins.toLocaleString()} Moedas`
                            )}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              <div className="space-y-3">
                <h3 className="font-bold text-base sm:text-lg">📜 Ganhadores de Ontem</h3>
                {winnersHistory.length === 0 ? (
                  <p className="text-center text-gray-500 py-4 text-sm">Nenhum ganhador ontem</p>
                ) : (
                  <div className="border rounded-lg p-3 bg-gray-50">
                    <p className="text-xs text-gray-500 mb-3">
                      📅 {winnersHistory[0]?.date.split('-').reverse().join('/')}
                    </p>
                    <div className="space-y-2">
                      {winnersHistory.map((winner) => {
                    const medal = getMedalIcon(winner.position);
                    const MedalIcon = medal.icon;
                    const isCurrentUser = winner.kako_id === kakoId;

                    return (
                      <div
                        key={winner.id}
                        className={`
                          p-2 rounded-lg
                          ${winner.position === 1 ? 'bg-gradient-to-br from-yellow-100 to-yellow-200 border-2 border-yellow-400 light-sweep' : 
                            winner.position === 2 ? 'bg-gradient-to-br from-gray-200 to-gray-300 border-2 border-gray-400 light-sweep' : 
                            winner.position === 3 ? 'bg-gradient-to-br from-orange-100 to-orange-200 border-2 border-orange-400 light-sweep' : 
                            'bg-white border border-gray-200'}
                          ${isCurrentUser ? 'ring-2 ring-purple-500' : ''}
                        `}
                      >
                        <div className="flex items-center gap-2 sm:gap-3">
                          {/* Avatar com moldura para top 3 */}
                          {winner.kako_avatar && (
                            winner.position === 1 ? (
                              <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center flex-shrink-0">
                                <img src={winner.kako_avatar} alt={winner.kako_nickname} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full absolute z-0" />
                                <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/xxtsr3u6_Emblema%20de%20Ouro%20e%20Coroa%20Real.png" alt="1º" className="w-full h-full object-contain absolute inset-0 z-10" />
                              </div>
                            ) : winner.position === 2 ? (
                              <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center flex-shrink-0">
                                <img 
                                  src={winner.kako_avatar} 
                                  alt={winner.kako_nickname} 
                                  className="rounded-full absolute z-0" 
                                  style={{ marginTop: '-8px', width: '38px', height: '38px' }}
                                />
                                <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/ebjdmsbn_IMG_7340.PNG" alt="2º" className="w-full h-full object-contain absolute inset-0 z-10" />
                              </div>
                            ) : winner.position === 3 ? (
                              <div className="relative w-20 h-20 sm:w-24 sm:h-24 flex items-center justify-center flex-shrink-0">
                                <img src={winner.kako_avatar} alt={winner.kako_nickname} className="w-8 h-8 sm:w-10 sm:h-10 rounded-full absolute z-0" style={{ marginTop: '-14px' }} />
                                <img src="https://customer-assets.emergentagent.com/job_kako-payments/artifacts/ez6yownk_IMG_7341.PNG" alt="3º" className="w-full h-full object-contain absolute inset-0 z-10" />
                              </div>
                            ) : (
                              <img
                                src={winner.kako_avatar}
                                alt={winner.kako_nickname}
                                className="w-8 h-8 sm:w-10 sm:h-10 rounded-full border-2 border-white shadow flex-shrink-0"
                              />
                            )
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1 flex-wrap">
                              <span className="font-medium text-sm sm:text-base truncate">{winner.kako_nickname}</span>
                              {isCurrentUser && <Badge className="text-[10px] sm:text-xs bg-purple-600">VOCÊ</Badge>}
                            </div>
                            <p className="text-[10px] sm:text-xs text-gray-600">
                              {winner.position}º lugar
                            </p>
                            <p className="text-[10px] sm:text-xs text-gray-600">{winner.total_coins.toLocaleString()} 🪙</p>
                          </div>
                          <div className="text-right flex-shrink-0">
                            <Badge className={`text-[10px] sm:text-xs ${winner.prize_claimed ? 'bg-green-600' : 'bg-gray-400'}`}>
                              {winner.prize_claimed ? '✓' : '✗'}
                            </Badge>
                            {winner.prize_coins && (
                              <p className="text-[10px] sm:text-xs text-gray-600 mt-1">+{winner.prize_coins.toLocaleString()} 🎁</p>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </TabsContent>
          )}
        </Tabs>
        ) : null}
      </CardContent>

      {/* Dialog de Regras - Otimizado para Mobile */}
      <Dialog open={showRules} onOpenChange={setShowRules}>
        <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto sm:max-w-lg">
          <DialogHeader className="sticky top-0 bg-white pb-4 border-b">
            <DialogTitle className="text-base sm:text-lg pr-8">
              <div className="flex items-center gap-2">
                <Trophy className="text-yellow-500 w-5 h-5" />
                Regras do Ranking
              </div>
            </DialogTitle>
            <DialogDescription className="text-xs sm:text-sm">
              Como funciona a competição
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-xs sm:text-sm">
            <div>
              <h4 className="font-bold mb-2 text-sm">📊 Como Funciona:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-xs sm:text-sm">
                <li>Ranking é DIÁRIO (reseta à meia-noite)</li>
                <li>Baseado no total de moedas do dia</li>
                <li>Pedidos com "Participar" contam</li>
                <li>Top 10 aparecem no ranking</li>
                <li>Prêmios aparecem ao lado da sua posição</li>
                <li className="font-bold text-purple-600">⭐ Quanto mais recarregar no dia, maior seu prêmio e posição no ranking!</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-2 text-sm">⏰ Resgate:</h4>
              <ul className="list-disc list-inside space-y-1 text-gray-600 text-xs sm:text-sm">
                <li>24h para resgatar</li>
                <li>Aba "Meus Prêmios"</li>
                <li>Perde se não resgatar</li>
              </ul>
            </div>

            <div>
              <h4 className="font-bold mb-2 text-sm">🔒 Privacidade:</h4>
              <p className="text-gray-600 text-xs sm:text-sm">
                Você pode não participar do ranking ao fazer recarga.
              </p>
            </div>

            {/* Botão Fechar Mobile */}
            <Button
              onClick={() => setShowRules(false)}
              className="w-full bg-purple-600 hover:bg-purple-700 sm:hidden"
            >
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default ClientRanking;
